import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DownloadOutlined, Inventory2Outlined } from '@mui/icons-material';
import { downloadFileFromUrl } from '../../lib/downloadFile';
import {
  addAdminOrderNote,
  getAdminOrder,
  getAdminOrderArtifactViewUrl,
  getAdminPrintPackageViewUrl,
  listAdminOrders,
  updateAdminOrderStatus,
} from '../../services/adminOrdersApi';

const STATUS_OPTIONS = ['Pending', 'InProgress', 'Delivering', 'Done'];
const TYPE_OPTIONS = [
  { value: 'translation', label: 'Notarised translation' },
  { value: 'visa_appointment', label: 'Visa appointment assistance' },
  { value: 'print_package', label: 'Print visa package' },
  { value: 'print_photos', label: 'Print photos' },
];

function formatOrderType(value) {
  return TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value ?? 'Order';
}

function formatStatusLabel(value) {
  return {
    Pending: 'Pending',
    InProgress: 'In progress',
    Delivering: 'Delivering',
    Done: 'Done',
  }[value] ?? value ?? 'Unknown';
}

function formatStatusColor(value) {
  return {
    Pending: 'warning',
    InProgress: 'info',
    Delivering: 'secondary',
    Done: 'success',
  }[value] ?? 'default';
}

function formatDateTime(value) {
  if (!value) {
    return 'Unknown';
  }

  const parsedValue = new Date(value);
  if (Number.isNaN(parsedValue.getTime())) {
    return value;
  }

  return parsedValue.toLocaleString();
}

function formatMoney(amount) {
  return `${amount ?? 0} EGP`;
}

function isPrintPackageOrder(value) {
  return value === 'print_package';
}

function buildHeadline(order) {
  return (
    order?.sourceRequirementTitle ||
    order?.metadata?.packageLabel ||
    order?.metadata?.photoLabel ||
    order?.accountEmail ||
    order?.accountPhoneNumber ||
    order?.orderId ||
    'Order'
  );
}

function matchesSearch(order, query) {
  if (!query) {
    return true;
  }

  const haystack = [
    order.orderId,
    order.accountId,
    order.accountPhoneNumber,
    order.accountEmail,
    order.sourceRequirementTitle,
    order.metadata?.packageLabel,
    order.metadata?.photoLabel,
    order.type,
    order.status,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(query);
}

function AdminOrdersWorkspace({ account }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [screenError, setScreenError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [accountFilter, setAccountFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [statusDraft, setStatusDraft] = useState('');
  const [statusBusy, setStatusBusy] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [artifactBusyKey, setArtifactBusyKey] = useState('');

  const loadOrders = useCallback(async () => {
    if (!account?.isAdmin) {
      setOrders([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setScreenError('');

    try {
      const response = await listAdminOrders({
        statuses: statusFilter === 'all' ? [] : [statusFilter],
        types: typeFilter === 'all' ? [] : [typeFilter],
        accountId: accountFilter,
      });

      setOrders(response.orders ?? []);
    } catch (error) {
      setScreenError(error.message);
    } finally {
      setLoading(false);
    }
  }, [account?.isAdmin, accountFilter, statusFilter, typeFilter]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const visibleOrders = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return orders.filter((order) => matchesSearch(order, normalizedSearch));
  }, [orders, search]);

  async function openOrder(orderId) {
    setDetailsLoading(true);
    setDetailsError('');

    try {
      const response = await getAdminOrder({ orderId });
      setSelectedOrder(response.order ?? null);
      setStatusDraft(response.order?.status ?? '');
      setNoteDraft('');
    } catch (error) {
      setDetailsError(error.message);
    } finally {
      setDetailsLoading(false);
    }
  }

  async function handleStatusSave() {
    if (!selectedOrder?.orderId || !statusDraft || statusDraft === selectedOrder.status) {
      return;
    }

    setStatusBusy(true);
    setDetailsError('');

    try {
      const updatedOrder = await updateAdminOrderStatus({
        orderId: selectedOrder.orderId,
        status: statusDraft,
      });

      setSelectedOrder(updatedOrder);
      setOrders((current) =>
        current.map((order) => (order.orderId === updatedOrder.orderId ? updatedOrder : order))
      );
      setSuccessMessage('Order status updated.');
    } catch (error) {
      setDetailsError(error.message);
    } finally {
      setStatusBusy(false);
    }
  }

  async function handleAddNote() {
    if (!selectedOrder?.orderId || !noteDraft.trim()) {
      return;
    }

    setNoteBusy(true);
    setDetailsError('');

    try {
      await addAdminOrderNote({
        orderId: selectedOrder.orderId,
        message: noteDraft,
      });

      const response = await getAdminOrder({ orderId: selectedOrder.orderId });
      setSelectedOrder(response.order ?? null);
      setOrders((current) =>
        current.map((order) => (order.orderId === response.order?.orderId ? response.order : order))
      );
      setNoteDraft('');
      setSuccessMessage('Admin note added.');
    } catch (error) {
      setDetailsError(error.message);
    } finally {
      setNoteBusy(false);
    }
  }

  async function handleArtifactOpen(document) {
    const artifactKey = `${document.memberId}:${document.documentId}`;
    setArtifactBusyKey(artifactKey);
    setDetailsError('');

    try {
      const response = await getAdminOrderArtifactViewUrl({
        orderId: selectedOrder.orderId,
        memberId: document.memberId,
        documentId: document.documentId,
      });

      downloadFileFromUrl(response.viewUrl, response.fileName);
    } catch (error) {
      setDetailsError(error.message);
    } finally {
      setArtifactBusyKey('');
    }
  }

  async function handlePrintPackageOpen() {
    if (!selectedOrder?.orderId) {
      return;
    }

    setArtifactBusyKey('package-zip');
    setDetailsError('');

    try {
      const response = await getAdminPrintPackageViewUrl({
        orderId: selectedOrder.orderId,
      });

      downloadFileFromUrl(response.viewUrl, response.fileName ?? 'visa-package.zip');
    } catch (error) {
      setDetailsError(error.message);
    } finally {
      setArtifactBusyKey('');
    }
  }

  if (!account?.isAdmin) {
    return (
      <Alert severity="warning">
        Admin access is required to view this workspace.
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      {screenError ? <Alert severity="error">{screenError}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      <Paper sx={{ p: 2.5 }} variant="outlined">
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            label="Search"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Order ID, email, phone, package label..."
            value={search}
          />
          <TextField
            label="Status"
            onChange={(event) => setStatusFilter(event.target.value)}
            select
            sx={{ minWidth: { lg: 180 } }}
            value={statusFilter}
          >
            <MenuItem value="all">All statuses</MenuItem>
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {formatStatusLabel(status)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Type"
            onChange={(event) => setTypeFilter(event.target.value)}
            select
            sx={{ minWidth: { lg: 220 } }}
            value={typeFilter}
          >
            <MenuItem value="all">All types</MenuItem>
            {TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Account ID"
            onChange={(event) => setAccountFilter(event.target.value)}
            sx={{ minWidth: { lg: 220 } }}
            value={accountFilter}
          />
          <Button onClick={() => void loadOrders()} variant="outlined">
            Refresh
          </Button>
        </Stack>
      </Paper>

      {loading ? (
        <Paper sx={{ p: 3 }} variant="outlined">
          <Stack alignItems="center" spacing={1.5}>
            <CircularProgress size={24} />
            <Typography color="text.secondary" variant="body2">
              Loading all orders...
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleOrders.length > 0 ? (
                visibleOrders.map((order) => (
                  <TableRow
                    hover
                    key={order.orderId}
                    onClick={() => void openOrder(order.orderId)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ minWidth: 280 }}>
                      <Stack alignItems="center" direction="row" spacing={1.5}>
                        <Box
                          sx={{
                            alignItems: 'center',
                            bgcolor: 'background.default',
                            borderRadius: 1.5,
                            color: 'text.secondary',
                            display: 'flex',
                            height: 36,
                            justifyContent: 'center',
                            width: 36,
                          }}
                        >
                          <Inventory2Outlined fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600 }}>{buildHeadline(order)}</Typography>
                          <Typography color="text.secondary" noWrap variant="body2">
                            {order.orderId}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Typography variant="body2">{order.accountEmail || 'No email'}</Typography>
                      <Typography color="text.secondary" variant="body2">
                        {order.accountPhoneNumber || order.accountId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        color={formatStatusColor(order.status)}
                        label={formatStatusLabel(order.status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatOrderType(order.type)}</TableCell>
                    <TableCell>{formatMoney(order.amountBreakdown?.totalEgp)}</TableCell>
                    <TableCell>{formatDateTime(order.updatedAtUtc)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary" variant="body2">
                      No orders matched the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog fullWidth maxWidth="md" onClose={() => setSelectedOrder(null)} open={Boolean(selectedOrder) || detailsLoading}>
        <DialogTitle>Admin order details</DialogTitle>
        <DialogContent dividers>
          {detailsLoading ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
              <CircularProgress size={24} />
              <Typography color="text.secondary" variant="body2">
                Loading order details...
              </Typography>
            </Stack>
          ) : selectedOrder ? (
            <Stack spacing={2.5}>
              {detailsError ? <Alert severity="error">{detailsError}</Alert> : null}

              <Paper sx={{ p: 2.5 }} variant="outlined">
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Account</Typography>
                  <Typography color="text.secondary" variant="body2">
                    Account ID: {selectedOrder.accountId}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Email: {selectedOrder.accountEmail || 'Not available'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Phone: {selectedOrder.accountPhoneNumber || 'Not available'}
                  </Typography>
                </Stack>
              </Paper>

              <Paper sx={{ p: 2.5 }} variant="outlined">
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                  <TextField
                    label="Order status"
                    onChange={(event) => setStatusDraft(event.target.value)}
                    select
                    sx={{ minWidth: { md: 220 } }}
                    value={statusDraft}
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <MenuItem key={status} value={status}>
                        {formatStatusLabel(status)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    disabled={statusBusy || !statusDraft || statusDraft === selectedOrder.status}
                    onClick={() => void handleStatusSave()}
                    variant="contained"
                  >
                    {statusBusy ? 'Saving...' : 'Save status'}
                  </Button>
                </Stack>
                <Stack sx={{ mt: 2 }} spacing={0.75}>
                  <Typography color="text.secondary" variant="body2">
                    Type: {formatOrderType(selectedOrder.type)}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Created: {formatDateTime(selectedOrder.createdAtUtc)}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Updated: {formatDateTime(selectedOrder.updatedAtUtc)}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Total: {formatMoney(selectedOrder.amountBreakdown?.totalEgp)}
                  </Typography>
                </Stack>
              </Paper>

              <Paper sx={{ p: 2.5 }} variant="outlined">
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Delivery and metadata</Typography>
                  <Typography color="text.secondary" variant="body2">
                    Address: {selectedOrder.deliveryDetails?.address || 'Not provided'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Phone: {selectedOrder.deliveryDetails?.phoneNumber || 'Not provided'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Estimated delivery day: {selectedOrder.deliveryDetails?.estimatedDeliveryDate || 'Not applicable'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Reference: {buildHeadline(selectedOrder)}
                  </Typography>
                </Stack>
              </Paper>

              <Paper sx={{ p: 2.5 }} variant="outlined">
                <Stack spacing={1.25}>
                  <Typography variant="subtitle2">Artifacts</Typography>
                  {isPrintPackageOrder(selectedOrder.type) ? (
                    <Box>
                      <Button
                        disabled={artifactBusyKey === 'package-zip'}
                        onClick={() => void handlePrintPackageOpen()}
                        startIcon={<DownloadOutlined />}
                        variant="contained"
                      >
                        {artifactBusyKey === 'package-zip' ? 'Preparing...' : 'Download package zip'}
                      </Button>
                    </Box>
                  ) : null}
                  {selectedOrder.requestedDocuments?.length > 0 ? (
                    selectedOrder.requestedDocuments.map((document) => {
                      const artifactKey = `${document.memberId}:${document.documentId}`;

                      return (
                        <Box
                          key={artifactKey}
                          sx={{
                            alignItems: { xs: 'flex-start', md: 'center' },
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 1.25,
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2">{document.fileName}</Typography>
                            <Typography color="text.secondary" variant="body2">
                              {document.memberId} • {document.pageCount} page{document.pageCount === 1 ? '' : 's'}
                            </Typography>
                          </Box>
                          {!isPrintPackageOrder(selectedOrder.type) ? (
                            <Button
                              disabled={artifactBusyKey === artifactKey}
                              onClick={() => void handleArtifactOpen(document)}
                              size="small"
                              startIcon={<DownloadOutlined />}
                              variant="outlined"
                            >
                              {artifactBusyKey === artifactKey ? 'Preparing...' : 'Download artifact'}
                            </Button>
                          ) : null}
                        </Box>
                      );
                    })
                  ) : (
                    <Typography color="text.secondary" variant="body2">
                      No requested documents are attached to this order.
                    </Typography>
                  )}
                </Stack>
              </Paper>

              <Paper sx={{ p: 2.5 }} variant="outlined">
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2">Admin notes</Typography>
                  <TextField
                    fullWidth
                    label="Add internal note"
                    multiline
                    minRows={3}
                    onChange={(event) => setNoteDraft(event.target.value)}
                    value={noteDraft}
                  />
                  <Box>
                    <Button
                      disabled={noteBusy || !noteDraft.trim()}
                      onClick={() => void handleAddNote()}
                      variant="contained"
                    >
                      {noteBusy ? 'Saving...' : 'Add note'}
                    </Button>
                  </Box>
                  <Stack spacing={1}>
                    {(selectedOrder.adminNotes ?? []).length > 0 ? (
                      selectedOrder.adminNotes.map((note) => (
                        <Paper key={note.noteId} sx={{ p: 1.5 }} variant="outlined">
                          <Typography variant="body2">{note.message}</Typography>
                          <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="caption">
                            {note.authorAccountId} • {formatDateTime(note.createdAtUtc)}
                          </Typography>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        No admin notes yet.
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          ) : (
            <Alert severity="error">{detailsError || 'Order details are unavailable.'}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSelectedOrder(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default AdminOrdersWorkspace;
